/* eslint-disable global-require */
import React, {
  CSSProperties, memo, useCallback, useRef, useState, useEffect,
} from 'react';
import {
  GoogleMap, Marker, OverlayView, Polygon,
} from '@react-google-maps/api';
import { Box, Typography } from '@mui/material';

const containerStyle: CSSProperties = {
  display: 'flex',
  flex: 1,
  height: '100%',
  position: 'relative',
};

const infoBoxStyle: CSSProperties = {
  position: 'absolute',
  top: '10px',
  left: '15%',
  fontSize: '12px',
  transform: 'translateX(-3%)',
  backgroundColor: 'white',
  padding: '4px 8px',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
  zIndex: 10,
  maxWidth: '250px',
};

type EntityToDraw = google.maps.Polygon | google.maps.Marker;

const DEFAULT_FIT_PADDING = 40;

const polygonColor = (isSelected?: boolean, isLinked?: boolean) => {
  if (isSelected) {
    return '#3693D5';
  } if (isLinked) {
    return '#54B03A';
  }
  return '#FFFFFF';
};

interface SectorInfo {
  data: Record<string, string>;
  customButton?: React.ReactNode;
}

function GoogleMaps({
  lat, lng, drawable, polygons, markers, setCoordinates, handleCenterChange, farm, handleZoomChange,
  sectorInfo, autoFit, fitBoundsPadding,
}: {
  lat: string | number,
  lng: string | number,
  drawable: 'polygon' | 'marker' | 'none',
  farm?: {
    id: string,
    coordinates: { lat: number, lng: number }[] | null,
    zoom?: number,
  },
  polygons?: {
    id?: string,
    coordinates?: { lat: number, lng: number }[],
    isSelected?: boolean,
    isLinked?: boolean,
    onPolygonClick?: VoidFunction,
    fillColor?: string,
    strokeColor?: string,
    strokeWeight?: number,
    fillOpacity?: number,
    badge?: {
      humidity?: number | null,
      temperature?: number | null,
      alerts?: number,
      accent?: string,
      textMain?: string,
      icon?: string,
    },
  }[]
  markers?: {
    id?: string,
    coordinates?: { lat: number, lng: number },
    isSelected?: boolean,
    isGatewayNode?: boolean,
    onMarkerClick?: VoidFunction,
  }[],
  setCoordinates?: React.Dispatch<React.SetStateAction<{ lng: number, lat: number }[] | null>>
  handleCenterChange?: (centerCoordinates: {
    lat: () => string;
    lng: () => string;
  }) => void,
  handleZoomChange?: (zoom: number) => void,
  sectorInfo?: SectorInfo | null,
  autoFit?: boolean,
  fitBoundsPadding?: { top?: number, right?: number, bottom?: number, left?: number },
}) {
  const mapRef = useRef<google.maps.Map | null>(null);
  const [scaleFactor, setScaleFactor] = useState<number>(1);
  const [mapReady, setMapReady] = useState(false);

  const verticesRef = useRef<{ lat: number; lng: number }[]>([]);
  const previewPolylineRef = useRef<google.maps.Polyline | null>(null);
  const drawnEntityRef = useRef<EntityToDraw | null>(null);
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const detectBrowserZoom = () => {
      const zoom = window.devicePixelRatio;
      const newScaleFactor = Math.max(0.5, 1 / zoom);
      setScaleFactor(newScaleFactor);
    };

    detectBrowserZoom();

    window.addEventListener('resize', detectBrowserZoom);

    return () => {
      window.removeEventListener('resize', detectBrowserZoom);
    };
  }, []);

  const onLoad = useCallback((mapElem: google.maps.Map) => {
    mapRef.current = mapElem;
    setMapReady(true);

    if (drawable === 'none') return;

    if (drawable === 'polygon') {
      const addVertex = (latLng: google.maps.LatLng) => {
        if (drawnEntityRef.current) return;
        const vertex = { lat: latLng.lat(), lng: latLng.lng() };
        verticesRef.current = [...verticesRef.current, vertex];
        if (setCoordinates) {
          setCoordinates([...verticesRef.current]);
        }

        const previewPath = [...verticesRef.current, verticesRef.current[0]];
        if (previewPolylineRef.current) {
          previewPolylineRef.current.setPath(previewPath);
        } else {
          previewPolylineRef.current = new window.google.maps.Polyline({
            path: previewPath,
            strokeColor: '#FF0000',
            strokeOpacity: 0.8,
            strokeWeight: 2,
            map: mapElem,
            geodesic: true,
          });
        }
      };

      const completePolygon = () => {
        if (verticesRef.current.length < 3) return;
        if (previewPolylineRef.current) {
          previewPolylineRef.current.setMap(null);
          previewPolylineRef.current = null;
        }
        const polygon = new window.google.maps.Polygon({
          paths: verticesRef.current,
          fillColor: '#FF0000',
          fillOpacity: 0.5,
          strokeWeight: 2,
          strokeColor: '#FF0000',
          clickable: true,
          editable: true,
          zIndex: 1,
          geodesic: true,
          map: mapElem,
        });
        drawnEntityRef.current = polygon;
        if (setCoordinates) {
          setCoordinates([...verticesRef.current]);
        }
        window.google.maps.event.addListener(polygon, 'rightclick', () => {
          polygon.setMap(null);
          drawnEntityRef.current = null;
          verticesRef.current = [];
          if (setCoordinates) setCoordinates(null);
        });
      };

      window.google.maps.event.addListener(
        mapElem,
        'click',
        (e: google.maps.MapMouseEvent) => {
          if (!e.latLng || drawnEntityRef.current) return;
          if (clickTimerRef.current !== null) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
            return;
          }
          const capturedLatLng = e.latLng;
          clickTimerRef.current = setTimeout(() => {
            clickTimerRef.current = null;
            addVertex(capturedLatLng);
          }, 200);
        },
      );

      window.google.maps.event.addListener(
        mapElem,
        'dblclick',
        (e: google.maps.MapMouseEvent) => {
          e.stop();
          if (clickTimerRef.current !== null) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
          }
          completePolygon();
        },
      );
    }

    if (drawable === 'marker') {
      window.google.maps.event.addListener(
        mapElem,
        'click',
        (e: google.maps.MapMouseEvent) => {
          if (!e.latLng) return;
          if (drawnEntityRef.current) {
            (drawnEntityRef.current as google.maps.Marker).setPosition(e.latLng);
          } else {
            const marker = new window.google.maps.Marker({
              position: e.latLng,
              map: mapElem,
            });
            drawnEntityRef.current = marker;
            window.google.maps.event.addListener(marker, 'rightclick', () => {
              marker.setMap(null);
              drawnEntityRef.current = null;
              if (setCoordinates) setCoordinates(null);
            });
          }
          if (setCoordinates) {
            setCoordinates([{ lat: e.latLng.lat(), lng: e.latLng.lng() }]);
          }
        },
      );
    }
  }, []);

  // Coordenadas a encuadrar con autoFit (polígonos + perímetro del campo).
  const fitCoords: { lat: number, lng: number }[] = [];
  if (autoFit) {
    (polygons || []).forEach((p) => {
      (p.coordinates || []).forEach((c) => {
        if (typeof c?.lat === 'number' && typeof c?.lng === 'number') fitCoords.push(c);
      });
    });
    (farm?.coordinates || []).forEach((c) => {
      if (typeof c?.lat === 'number' && typeof c?.lng === 'number') fitCoords.push(c);
    });
  }
  // Firma de la geometría: el efecto de autoFit sólo debe re-encuadrar cuando
  // cambian las coordenadas reales, no en cada render del padre (los arrays de
  // polígonos se recrean con nueva identidad y pisarían el zoom/pan del usuario).
  const fitCoordsSignature = fitCoords.map((c) => `${c.lat},${c.lng}`).join(';');

  // Ajuste automático: si autoFit, recalcula bounds para englobar todos los polígonos
  // cuando cambia la geometría o el padding (ej: se abre/cierra el panel lateral).
  useEffect(() => {
    if (!autoFit || !mapReady || !mapRef.current || !window?.google?.maps) return;
    if (fitCoords.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    fitCoords.forEach((c) => bounds.extend(c));
    const padding = {
      top: fitBoundsPadding?.top ?? DEFAULT_FIT_PADDING,
      right: fitBoundsPadding?.right ?? DEFAULT_FIT_PADDING,
      bottom: fitBoundsPadding?.bottom ?? DEFAULT_FIT_PADDING,
      left: fitBoundsPadding?.left ?? DEFAULT_FIT_PADDING,
    };
    mapRef.current.fitBounds(bounds, padding);
  }, [
    autoFit,
    mapReady,
    fitCoordsSignature,
    fitBoundsPadding?.top,
    fitBoundsPadding?.right,
    fitBoundsPadding?.bottom,
    fitBoundsPadding?.left,
  ]);

  const onUnmount = useCallback(() => {
    if (clickTimerRef.current !== null) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    if (previewPolylineRef.current) {
      previewPolylineRef.current.setMap(null);
      previewPolylineRef.current = null;
    }
    if (drawnEntityRef.current) {
      drawnEntityRef.current.setMap(null);
      drawnEntityRef.current = null;
    }
    if (mapRef.current) {
      window.google.maps.event.clearListeners(mapRef.current, 'click');
      window.google.maps.event.clearListeners(mapRef.current, 'dblclick');
      mapRef.current = null;
    }
  }, []);

  const parsedLat = typeof lat === 'string' ? parseFloat(lat) : lat;
  const parsedLng = typeof lng === 'string' ? parseFloat(lng) : lng;

  if (Number.isNaN(parsedLat) || Number.isNaN(parsedLng)) {
    return <Box style={containerStyle} />;
  }

  const dynamicSectorInfoStyle: CSSProperties = {
    position: 'absolute',
    top: '10px',
    left: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: `${15 * scaleFactor}px`,
    borderRadius: '4px',
    zIndex: 1000,
    width: `${400 * scaleFactor}px`,
    boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
  };

  const infoBoxText = drawable === 'polygon'
    ? 'Click to add vertices, double-click to complete. Right-click to delete.'
    : 'Click to place marker. Right-click to delete.';

  return (
    <Box style={containerStyle}>
      {sectorInfo && (
        <Box style={dynamicSectorInfoStyle}>
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            gutterBottom
          >
            Resumen sector
          </Typography>
          {Object.entries(sectorInfo.data).map(([key, value]) => (
            <Box key={key} sx={{ marginBottom: '4px' }}>
              <Typography
                variant="body2"
                component="span"
                color="textSecondary"
              >
                {key}:{' '}
              </Typography>
              <Typography
                variant="body2"
                component="span"
              >
                {value}
              </Typography>
            </Box>
          ))}
          {sectorInfo.customButton}
        </Box>
      )}
      <GoogleMap
        mapContainerStyle={{ flex: 1, height: '100%' }}
        center={{
          lat: parsedLat,
          lng: parsedLng,
        }}
        zoom={farm?.zoom ?? 18}
        options={{
          mapTypeControl: false,
          mapTypeId: 'satellite',
          streetViewControl: false,
        }}
        onLoad={onLoad}
        onUnmount={onUnmount}
        onCenterChanged={() => {
          if (handleCenterChange && mapRef.current) {
            const center = mapRef.current.getCenter();
            if (center) {
              handleCenterChange({
                lat: () => center.lat().toString(),
                lng: () => center.lng().toString(),
              });
            }
          }
        }}
        onZoomChanged={() => {
          if (handleZoomChange && mapRef.current) {
            const currentZoom = mapRef.current.getZoom() as number;
            handleZoomChange(currentZoom);
          }
        }}
      >
        {farm && farm.coordinates && <Polygon
          key={farm.id}
          path={farm.coordinates.map((coord) => ({
            lat: coord.lat,
            lng: coord.lng,
          }))}
          options={{
            strokeColor: '#FFFFFF',
            strokeWeight: 4,
          }}
        />}

        {polygons && polygons.map((polygon) => (
          <Polygon
            onClick={polygon.onPolygonClick}
            key={polygon.id}
            path={polygon.coordinates && polygon.coordinates.map((coord) => ({
              lat: coord.lat,
              lng: coord.lng,
            }))}
            options={{
              fillColor: polygon.fillColor || polygonColor(polygon.isSelected, polygon.isLinked),
              fillOpacity: polygon.fillOpacity ?? 0.4,
              strokeColor: polygon.strokeColor
                || polygon.fillColor
                || polygonColor(polygon.isSelected, polygon.isLinked),
              strokeOpacity: 0.8,
              strokeWeight: polygon.strokeWeight ?? 2,
            }}
          />
        ))}
        {polygons && polygons.map((polygon) => {
          if (!polygon.badge || !polygon.coordinates || polygon.coordinates.length === 0) {
            return null;
          }
          const validCoords = polygon.coordinates.filter(
            (c) => typeof c?.lat === 'number' && typeof c?.lng === 'number',
          );
          if (validCoords.length === 0) return null;
          const avg = validCoords.reduce(
            (acc, c) => ({ lat: acc.lat + c.lat, lng: acc.lng + c.lng }),
            { lat: 0, lng: 0 },
          );
          const center = { lat: avg.lat / validCoords.length, lng: avg.lng / validCoords.length };
          const { badge } = polygon;
          const accent = badge.accent || '#003D7A';
          return (
            <OverlayView
              key={`badge-${polygon.id}`}
              position={center}
              mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
              getPixelPositionOffset={(w, h) => ({ x: -(w / 2), y: -(h / 2) })}
            >
              <div
                onClick={polygon.onPolygonClick}
                style={{
                  background: accent,
                  border: '2px solid #FFFFFF',
                  borderRadius: 10,
                  padding: '6px 10px',
                  fontSize: 13,
                  fontWeight: 800,
                  color: '#FFFFFF',
                  boxShadow: '0 4px 12px rgba(15,23,42,0.35)',
                  whiteSpace: 'nowrap',
                  cursor: 'pointer',
                  lineHeight: 1.2,
                  textAlign: 'center',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  letterSpacing: '0.01em',
                  pointerEvents: 'auto',
                  textShadow: '0 1px 2px rgba(0,0,0,0.25)',
                }}
              >
                {badge.textMain ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <span style={{ fontSize: 15 }}>{badge.icon || '📊'}</span>
                    {badge.textMain}
                  </span>
                ) : (
                  <>
                    {typeof badge.humidity === 'number' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 15 }}>💧</span>
                        {badge.humidity.toFixed(1)}%
                      </span>
                    )}
                    {typeof badge.temperature === 'number' && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <span style={{ fontSize: 15 }}>🌡️</span>
                        {badge.temperature.toFixed(1)}°C
                      </span>
                    )}
                  </>
                )}
                {!!badge.alerts && badge.alerts > 0 && (
                  <span style={{
                    background: '#FFFFFF',
                    color: '#DC2626',
                    borderRadius: 999,
                    padding: '1px 6px',
                    fontSize: 11,
                    fontWeight: 800,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 2,
                  }}>
                    ⚠ {badge.alerts}
                  </span>
                )}
              </div>
            </OverlayView>
          );
        })}
        {markers && markers.map((marker) => {
          if (marker.coordinates) {
            let iconToReturn = '';
            if ('nodeType' in marker) {
              if (marker.isGatewayNode) {
                iconToReturn = marker.isSelected
                  ? require('../../../assets/images/devicesMarkers/nodo_gw_selected.png')
                  : require('../../../assets/images/devicesMarkers/nodo_gw.png');
              } else {
                iconToReturn = marker.isSelected
                  ? require('../../../assets/images/devicesMarkers/nodo_selected.png')
                  : require('../../../assets/images/devicesMarkers/nodo.png');
              }
            } else if (!('nodeType' in marker)) {
              iconToReturn = marker.isSelected
                ? require('../../../assets/images/devicesMarkers/sensor_selected.png')
                : require('../../../assets/images/devicesMarkers/sensor.png');
            }

            return <Marker
              onClick={marker.onMarkerClick}
              key={marker.id}
              position={marker.coordinates}
              icon={iconToReturn}
            />;
          }
          return null;
        })}
      </GoogleMap>
      {drawable !== 'none' && <Box style={infoBoxStyle}>
        {infoBoxText}
      </Box>}

    </Box>

  );
}

export default memo(GoogleMaps);
